import * as React from "react"
import TextField from "@mui/material/TextField"

const MaskedCurrencyInput = React.forwardRef(({
  className,
  value,
  onChange,
  ...props
}, ref) => {
  const inputRef = React.useRef(null);
  const [displayValue, setDisplayValue] = React.useState('R$ 0,00');
  const [numericValue, setNumericValue] = React.useState(0);
  const isUpdatingFromProp = React.useRef(false);

  React.useImperativeHandle(ref, () => inputRef.current, []);

  const formatCurrency = (cents) => {
    const reais = Math.floor(cents / 100);
    const centavos = cents % 100;
    const formattedReais = reais.toLocaleString('pt-BR');
    return `R$ ${formattedReais},${centavos.toString().padStart(2, '0')}`;
  };

  const parseCurrency = (formatted) => {
    const numbers = formatted.replace(/[^\d]/g, '');
    return parseInt(numbers) || 0;
  };

  React.useEffect(() => {
    if (value !== undefined && !isUpdatingFromProp.current) {
      const cents = Math.round(value * 100);
      setNumericValue(cents);
      setDisplayValue(formatCurrency(cents));
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if ([8, 9, 27, 13, 46].includes(e.keyCode)) {
      if (e.keyCode === 8) { 
        e.preventDefault();
        const newCents = Math.floor(numericValue / 10);
        setNumericValue(newCents);
        setDisplayValue(formatCurrency(newCents));

        isUpdatingFromProp.current = true;
        onChange?.(newCents / 100);
        setTimeout(() => { isUpdatingFromProp.current = false }, 10);
      }
      return;
    }

    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const digit = e.keyCode >= 96 ? e.keyCode - 96 : e.keyCode - 48;
    const newCents = (numericValue * 10) + digit;
    if (newCents > 99999999) return;

    setNumericValue(newCents);
    setDisplayValue(formatCurrency(newCents));

    isUpdatingFromProp.current = true;
    onChange?.(newCents / 100);
    setTimeout(() => { isUpdatingFromProp.current = false }, 10);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numbers = pastedText.replace(/[^\d]/g, '');
    if (numbers) {
      const newCents = parseInt(numbers);
      if (newCents <= 99999999) {
        setNumericValue(newCents);
        setDisplayValue(formatCurrency(newCents));

        isUpdatingFromProp.current = true;
        onChange?.(newCents / 100);
        setTimeout(() => { isUpdatingFromProp.current = false }, 10);
      }
    }
  };

  return (
    <TextField
      inputRef={inputRef}
      type="text"
      value={displayValue}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder="R$ 0,00"
      className={className}
      {...props}
    />
  )
});

MaskedCurrencyInput.displayName = "MaskedCurrencyInput";

export { MaskedCurrencyInput };
